import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

const OrgChartPage = React.lazy(() => import("./OrgChartPage"));

/**
 * Org Chart Module Config
 *
 * Visualises the reporting hierarchy of human Employees + Digital Manager AI
 * agents in a single tree. Departments colour-coded from `Department.color`,
 * drag-drop reports-to reassignment for ORG_CHART_EDIT users, sidebar of
 * unassigned employees, permission-gated cross-module link buttons in the
 * node detail drawer.
 *
 * SINGLE SOURCE OF TRUTH for permissions: backend `org-chart/permissions.ts`.
 * Routes use `permissionLevel` ("view", "edit", "admin") and resolve at
 * runtime via the `modulePermissions` GraphQL query.
 */
const orgChartModuleConfig: ModuleConfig = {
    id: "org-chart",
    name: "Org Chart",
    description: "Reporting hierarchy of employees + digital managers with permission-gated cross-module links",
    version: "1.0.0",
    enabled: true,
    category: "business",
    order: 96,
    icon: "🌳",
    color: getColor("primaryBlue"),

    routes: [
        {
            path: "/org-chart",
            component: OrgChartPage,
            permissionLevel: "view",
            useLayout: true,
            // Org chart canvas needs the full page width AND vertical room — skip
            // the default Container (which caps at container.xl ~1280px and adds
            // horizontal padding) plus the breadcrumb row and footer that would
            // otherwise eat ~200px of vertical canvas space. The page handles
            // its own internal header so users still know where they are.
            layoutOptions: { noContainer: true, noBreadcrumb: true },
        },
    ],

    navigation: [
        {
            label: "Org Chart",
            path: "/org-chart",
            icon: "🌳",
            permissionLevel: "view",
        },
    ],

    // Required by ModuleConfig interface — empty per the canonical convention
    // (backend permissions.ts is the SSOT, surfaced via the modulePermissions
    // GraphQL query and resolved through `permissionLevel` above).
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        admin: [],
    },
};

export default orgChartModuleConfig;
